import { ShieldCheck, UserCheck, Gauge, LayoutDashboard, GraduationCap, PhoneCall, Boxes, ThumbsUp, Rocket } from "lucide-react";

const INK = "#0F1B2D";
const CYAN_DARK = "#0E509C";
const PINK_DARK = "#10346E";

const SUITE = [
  {
    Icon: UserCheck,
    t: "A dedicated compliance expert",
    b: "A named practitioner assigned to RTD who checks in on your cadence — weekly at first, monthly once you're humming. Not a ticket queue; a person who knows your firm and brings ideas before you ask.",
  },
  {
    Icon: ShieldCheck,
    t: "Regulatory / exam counsel",
    b: "SEC exam prep, document-request responses, deficiency-letter drafting, and the Advisers Act judgment calls — from someone who has sat across from examiners with the platform open.",
  },
  {
    Icon: Gauge,
    t: "Risk assessment & annual review",
    b: "A living, adviser-specific risk assessment that powers your Rule 206(4)-7 annual review — refreshed as services, custodians, and headcount change, not a PDF that ages in a drawer.",
  },
  {
    Icon: LayoutDashboard,
    t: "Compliance management system",
    b: "Policies, procedures & agreements as a single source of truth · issue management · Monitoring & Testing · regulatory filings · CompliMind + Expert Prompts · marketing review · Code of Ethics — everything included at one flat fee.",
  },
  {
    Icon: GraduationCap,
    t: "Training program",
    b: "Role-based training for advisors, client service, operations, and trading — Code of Ethics, Marketing Rule, privacy, senior-investor protection — custom modules in about a week, tracking and attestations built in.",
  },
];

const PATH = [
  { Icon: PhoneCall, n: "1", t: "Design your system here", b: "Use the Plan Builder below to shape the platform around how your team works — modules, add-ons, cadence — and share it for sign-off." },
  { Icon: Boxes, n: "2", t: "Demo next week — prototype included", b: "A live walkthrough of a prototype configured to RTD. You click through real workflows before anyone signs anything." },
  { Icon: ThumbsUp, n: "3", t: "Sign off on the demo · agree the timeline", b: "Once it feels right, we lock the plan and the go-live date together. No obligation until then." },
  { Icon: Rocket, n: "4", t: "Pay the install before quarter-end · live in 45 days", b: "Start using the platform immediately. The $1,000/mo begins only once you've fully transitioned off your current platform — never paying twice." },
];

export function PlatformSuite() {
  return (
    <section id="suite" className="anchor-target py-14 md:py-20">
      <span className="rt-eyebrow">The full suite · built for an SEC-registered adviser</span>
      <h2 className="rt-h text-4xl md:text-5xl mt-3 max-w-3xl" data-testid="suite-headline">
        A compliance partner that listens back — <span className="rt-grad-text">under one roof, at one price.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
        Five functions most advisers split across a software vendor, an outside consultant, and whoever has
        spare hours in operations. You get all of them from one team that already knows the Advisers Act — and
        the platform keeps improving because we build what our clients ask for.
      </p>
      <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SUITE.map(({ Icon, t, b }, i) => (
          <div key={t} className="rt-card p-6" data-testid={`suite-card-${i}`}>
            <div className="h-10 w-10 rounded-xl grid place-items-center mb-4"
                 style={{ background: "#4F86D633" }}>
              <Icon className="h-5 w-5" style={{ color: CYAN_DARK }} strokeWidth={1.8} />
            </div>
            <div className="rt-h text-lg mb-2" style={{ color: INK }}>{t}</div>
            <p className="text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
          </div>
        ))}
        <div className="rt-card p-6 flex flex-col justify-center" style={{ background: `${PINK_DARK}06`, borderColor: `${PINK_DARK}30` }}>
          <div className="rt-h text-lg mb-2" style={{ color: PINK_DARK }}>Why one roof?</div>
          <p className="text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.75 }}>
            Your expert, your counsel, your training, and your CMS all read from the same data.
            No hand-offs, no "that's the other vendor's module," no gaps for an examiner to find —
            and when the SEC asks a question, the answer is one search away.
          </p>
        </div>
      </div>
    </section>
  );
}

export function CommitmentPath() {
  return (
    <section id="path" className="anchor-target py-14 md:py-20">
      <span className="rt-eyebrow">How to work with us</span>
      <h2 className="rt-h text-4xl md:text-5xl mt-3 max-w-3xl">
        You pay once you know your compliance life <span className="rt-grad-text">is about to get easier.</span>
      </h2>
      <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PATH.map(({ Icon, n, t, b }) => (
          <div key={n} className="rt-card p-6 relative" data-testid={`path-step-${n}`}>
            <div className="absolute top-4 right-5 rt-h text-4xl" style={{ color: "#4F86D655" }}>{n}</div>
            <Icon className="h-5 w-5 mb-3" style={{ color: CYAN_DARK }} strokeWidth={1.8} />
            <div className="rt-h text-base mb-1.5" style={{ color: INK }}>{t}</div>
            <p className="text-[13px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 rt-card p-6 md:p-7" style={{ borderColor: `${CYAN_DARK}40`, background: "#4F86D612" }} data-testid="commitment-45">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="rt-h text-5xl flex-shrink-0" style={{ color: CYAN_DARK }}>45<span className="text-xl align-top ml-1">days</span></div>
          <div>
            <div className="rt-h text-lg" style={{ color: INK }}>The CompliSun Commitment</div>
            <p className="text-[14px] leading-relaxed mt-1" style={{ color: INK, opacity: 0.78 }}>
              Once you sign off on the demo, we commit to a fully configured, tested, and <strong>live</strong> compliance
              program — built around RTD Financial's actual workflows — within 45 days. If we miss that date for reasons
              within our control, <strong>your first 2 months of platform fees are on us.</strong> Throughout the transition
              our compliance experts run a gap analysis and advise at the discounted $150/hr rate — and your monthly fee
              doesn't start until you're fully off the other platform.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
